import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import ToothIcon from "@/components/ToothIcon";
import { ShieldCheck, AlertTriangle, LogIn } from "lucide-react";

export default function TenantWelcome() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { branding, loadPublic, refresh: refreshBranding } = useBranding();
  const [tenant, setTenant] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/tenants/by-slug/${slug}`).then((r) => {
      setTenant(r.data);
      setError("");
    }).catch((e) => {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Clínica no encontrada");
      setTenant(null);
    }).finally(() => setLoading(false));
    // Cargar branding público para personalizar el welcome
    loadPublic && loadPublic(slug);
  }, [slug, loadPublic]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const ok = await login(email, password, slug);
      if (ok) {
        await refreshBranding();
        navigate("/", { replace: true });
      } else setError("Credenciales inválidas");
    } catch {
      setError("Error iniciando sesión");
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-sm font-mono text-muted-foreground">Cargando…</div>
    </div>
  );

  if (!tenant) return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="card-flat p-8 max-w-md w-full text-center" data-testid="tenant-not-found">
        <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
        <h1 className="font-display font-bold text-2xl tracking-tight mb-2">Clínica no encontrada</h1>
        <p className="text-sm text-muted-foreground mb-6">
          No existe ninguna clínica registrada con el dominio <span className="font-mono font-bold text-foreground">{slug}</span>.
        </p>
        <p className="text-xs text-muted-foreground">{error}</p>
      </div>
    </div>
  );

  if (tenant.status === "suspended") return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="card-flat p-8 max-w-md w-full text-center" data-testid="tenant-suspended">
        <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
        <h1 className="font-display font-bold text-2xl tracking-tight mb-2">{tenant.name}</h1>
        <p className="text-sm text-muted-foreground mb-2">El acceso a esta clínica está temporalmente suspendido.</p>
        <p className="text-xs text-muted-foreground">Contacta al administrador de la plataforma para más información.</p>
      </div>
    </div>
  );

  if (tenant.expired) return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="card-flat p-8 max-w-md w-full text-center" data-testid="tenant-expired">
        <AlertTriangle className="w-12 h-12 text-accent mx-auto mb-4" />
        <h1 className="font-display font-bold text-2xl tracking-tight mb-2">{tenant.name}</h1>
        <p className="text-sm text-muted-foreground mb-2">La licencia de esta clínica ha expirado.</p>
        <p className="text-xs text-muted-foreground">Vencimiento: <span className="font-mono">{tenant.expires_at}</span></p>
        <p className="text-xs text-muted-foreground mt-4">Contacta al administrador de la plataforma para renovar.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md" data-testid="tenant-welcome">
        <div className="text-center mb-6">
          {branding.logo_principal_b64 ? (
            <img src={branding.logo_principal_b64} alt={branding.nombre_comercial}
              className="h-16 mx-auto mb-4 object-contain" />
          ) : branding.logo_cuadrado_b64 ? (
            <img src={branding.logo_cuadrado_b64} alt={branding.nombre_comercial}
              className="w-14 h-14 mx-auto mb-4 object-contain rounded-sm" />
          ) : (
            <div className="w-14 h-14 rounded-sm inline-flex items-center justify-center mb-4"
              style={{ background: "var(--brand-primary)", color: "#fff" }}>
              <ToothIcon className="w-8 h-8" />
            </div>
          )}
          <div className="label-eyebrow mb-2">Bienvenido a</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">{branding.nombre_comercial || tenant.name}</h1>
          {branding.eslogan && (
            <p className="text-sm text-muted-foreground mt-1 italic">{branding.eslogan}</p>
          )}
          <p className="text-xs text-muted-foreground mt-2 font-mono">{slug}.dentcloud</p>
        </div>

        <form onSubmit={submit} className="card-flat p-6 space-y-4">
          <div>
            <label className="label-eyebrow block mb-2">Usuario</label>
            <input type="text" required autoFocus autoComplete="username"
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              className="input-flat"
              value={email} onChange={(e) => setEmail(e.target.value)} data-testid="tenant-username"
              placeholder="marta" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Contraseña</label>
            <input type="password" required autoComplete="current-password" className="input-flat"
              value={password} onChange={(e) => setPassword(e.target.value)} data-testid="tenant-password" />
          </div>
          {error && (
            <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-sm p-2">
              {error}
            </div>
          )}
          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="tenant-login-btn">
            <LogIn className="w-4 h-4" /> {submitting ? "Ingresando…" : "Ingresar"}
          </button>
        </form>

        <div className="text-center mt-4 text-xs text-muted-foreground">
          DentCloud · Software para consultorios odontológicos
        </div>
      </div>
    </div>
  );
}
